import React, { createContext, useContext, useState, useCallback } from 'react';
import { debugLog, debugWarn, debugError } from '../utils/debugLog';
import { useAuth } from './AuthContext';
import {
  tenantService,
  featureService,
  themeService,
  navigationService,
} from './services';

const PlatformContext = createContext();

export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
};

export const PlatformProvider = ({ children }) => {
  // Get tenant from AuthContext (single source of truth)
  const { tenant } = useAuth();

  // Tenant state (for listing all tenants - admin function)
  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState(null);
  const [tenantPagination, setTenantPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  // Feature state
  const [features, setFeatures] = useState([]);
  const [tenantFeatures, setTenantFeatures] = useState([]);
  const [featureLoading, setFeatureLoading] = useState(false);

  // Theme state
  const [themes, setThemes] = useState([]);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(false);

  // Navigation state
  const [navigation, setNavigation] = useState([]);
  const [navigationLoading, setNavigationLoading] = useState(false);

  // User state (extended from AuthContext)
  const [user, setUser] = useState(null);
  const [userLoading] = useState(false);

  // Platform configuration
  const [platformConfig, setPlatformConfig] = useState({
    features: {
      promptManagement: true,
      biAnalytics: false,
      experiments: false,
      governance: true,
      multiTenant: true,
      apiAccess: true,
      customization: false,
    },
    theme: {
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      fontFamily: 'Inter',
      layout: 'standard',
    },
    navigation: {
      style: 'sidebar',
      collapsed: false,
      items: [],
    },
  });

  // NOTE: loadTenant() was removed - tenant information is now obtained
  // directly from AuthContext (JWT token), eliminating the need for a
  // separate API call to /api/v1/tenant

  // Load all tenants
  const loadTenants = useCallback(async (page = 1, pageSize = 10) => {
    setTenantLoading(true);
    try {
      const result = await tenantService.loadTenants(page, pageSize);
      setTenants(result.tenants);
      setTenantPagination(result.pagination);
      if (result.error) {
        setTenantError(result.error);
      }
      return result.tenants;
    } catch (error) {
      setTenantError(error.message);
      throw error;
    } finally {
      setTenantLoading(false);
    }
  }, []);

  // Load tenant features (tenant_id comes from auth context)
  const loadTenantFeatures = useCallback(async () => {
    setFeatureLoading(true);
    try {
      const features = await featureService.loadTenantFeatures();
      setTenantFeatures(features);
      return features;
    } catch (error) {
      throw error;
    } finally {
      setFeatureLoading(false);
    }
  }, []);

  // Load all available features
  const loadFeatures = useCallback(async () => {
    setFeatureLoading(true);
    try {
      const features = await featureService.loadFeatures();
      setFeatures(features);
      return features;
    } catch (error) {
      throw error;
    } finally {
      setFeatureLoading(false);
    }
  }, []);

  // Load tenant theme (tenant_id comes from auth context)
  const loadTenantTheme = useCallback(async () => {
    setThemeLoading(true);
    try {
      const theme = await themeService.loadTenantTheme();
      setCurrentTheme(theme);
      return theme;
    } catch (error) {
      setCurrentTheme(null);
      return null;
    } finally {
      setThemeLoading(false);
    }
  }, []);

  // Load all themes for a tenant (tenant_id comes from auth context)
  const loadTenantThemes = useCallback(async (page = 1, pageSize = 10) => {
    setThemeLoading(true);
    try {
      const transformedThemes = await themeService.loadTenantThemes(
        page,
        pageSize
      );
      setThemes(transformedThemes);
      return transformedThemes;
    } catch (error) {
      throw error;
    } finally {
      setThemeLoading(false);
    }
  }, []);

  // Load active theme (wrapper for loadTenantTheme)
  const loadActiveTheme = useCallback(async () => {
    return loadTenantTheme();
  }, [loadTenantTheme]);

  // Load tenant navigation (tenant_id comes from auth context)
  const loadTenantNavigation = useCallback(async () => {
    setNavigationLoading(true);
    try {
      const navigationItems = await navigationService.loadTenantNavigation();
      setNavigation(navigationItems);
      return navigationItems;
    } catch (error) {
      throw error;
    } finally {
      setNavigationLoading(false);
    }
  }, []);

  // Update tenant features (tenant_id comes from auth context)
  const updateTenantFeatures = async features => {
    try {
      const updatedFeatures =
        await featureService.updateTenantFeatures(features);
      setTenantFeatures(updatedFeatures);
      return updatedFeatures;
    } catch (error) {
      throw error;
    }
  };

  // Update tenant theme (tenant_id comes from auth context)
  const updateTenantTheme = async theme => {
    try {
      const updatedTheme = await themeService.updateTenantTheme(theme);
      setCurrentTheme(updatedTheme);
      return updatedTheme;
    } catch (error) {
      throw error;
    }
  };

  // Create new theme (tenant_id comes from auth context)
  const createTheme = async themeData => {
    try {
      const newTheme = await themeService.createTheme(themeData);
      setThemes(prevThemes => [...prevThemes, newTheme]);
      return newTheme;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating theme:', error);
      throw error;
    }
  };

  // Update theme by ID
  const updateTheme = async (themeId, themeData) => {
    try {
      const response = await themeService.updateTheme(themeId, themeData);
      // Update the theme in the themes list
      setThemes(prevThemes =>
        prevThemes.map(theme =>
          theme.theme_id === themeId ? { ...theme, ...themeData } : theme
        )
      );
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating theme:', error);
      throw error;
    }
  };

  // Delete theme by ID
  const deleteTheme = async themeId => {
    try {
      await themeService.deleteTheme(themeId);
      // Remove the theme from the themes list
      setThemes(prevThemes =>
        prevThemes.filter(theme => theme.theme_id !== themeId)
      );
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Activate theme by ID
  const activateTheme = async themeId => {
    try {
      const response = await themeService.activateTheme(themeId);
      // Update the theme status in the themes list
      setThemes(prevThemes =>
        prevThemes.map(theme =>
          theme.theme_id === themeId
            ? { ...theme, status: 'active', is_active: true }
            : { ...theme, is_active: false }
        )
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update tenant navigation (tenant_id comes from auth context)
  const updateTenantNavigation = async navigation => {
    try {
      const updatedNavigation =
        await navigationService.updateTenantNavigation(navigation);
      setNavigation(updatedNavigation);
      return updatedNavigation;
    } catch (error) {
      throw error;
    }
  };

  // Check if feature is enabled for current tenant (case-insensitive)
  // Uses tenant from AuthContext instead of currentTenant state
  const isFeatureEnabled = featureCode => {
    if (!tenant || !tenantFeatures.length || !featureCode) return false;
    return featureService.isFeatureEnabled(tenantFeatures, featureCode);
  };

  // Get feature configuration for current tenant (case-insensitive)
  // Uses tenant from AuthContext instead of currentTenant state
  const getFeatureConfig = featureCode => {
    if (!tenant || !tenantFeatures.length || !featureCode) return null;
    return featureService.getFeatureConfig(tenantFeatures, featureCode);
  };

  // Get theme configuration for current tenant
  const getThemeConfig = () => {
    return currentTheme || platformConfig.theme;
  };

  // Get navigation configuration for current tenant
  const getNavigationConfig = () => {
    return navigation.length > 0 ? navigation : platformConfig.navigation.items;
  };

  // Initialize platform context with timeout and error handling
  // Note: tenant_id now comes from auth context, not passed as parameter
  const initializePlatform = useCallback(async () => {
    try {
      // Create timeout promise for each API call
      // Increased timeout to 15 seconds to handle slow network conditions
      const withTimeout = (promise, timeoutMs = 15000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          ),
        ]);
      };

      // Parallelize all API calls with individual timeouts
      // Note: tenant_id now comes from auth context, not passed as parameter
      // NOTE: loadTenant() was removed - tenant info comes from AuthContext
      const results = await Promise.allSettled([
        withTimeout(loadTenantFeatures(), 15000),
        withTimeout(loadTenantTheme(), 15000),
        withTimeout(loadTenantNavigation(), 15000),
      ]);

      // Check if any critical calls failed
      const [featuresResult, themeResult, navigationResult] = results;

      if (featuresResult.status === 'rejected') {
        debugWarn('Failed to load tenant features:', featuresResult.reason);
      }
      if (themeResult.status === 'rejected') {
        debugWarn('Failed to load tenant theme:', themeResult.reason);
      }
      if (navigationResult.status === 'rejected') {
        debugWarn('Failed to load tenant navigation:', navigationResult.reason);
      }

      // Continue even if some calls fail - use defaults
      debugLog(
        'Platform initialization completed with some timeouts/failures handled'
      );
    } catch (error) {
      // Error initializing platform
      debugError('Platform initialization error:', error);
      throw error;
    }
  }, [loadTenantFeatures, loadTenantTheme, loadTenantNavigation]);

  // Context value
  const contextValue = {
    // Tenant
    // NOTE: currentTenant removed - use tenant from AuthContext instead
    currentTenant: tenant, // Alias for backward compatibility
    tenants,
    tenantLoading,
    tenantError,
    tenantPagination,
    // NOTE: loadTenant removed - tenant comes from AuthContext
    loadTenants,

    // Features
    features,
    tenantFeatures,
    featureLoading,
    loadFeatures,
    loadTenantFeatures,
    updateTenantFeatures,
    isFeatureEnabled,
    getFeatureConfig,

    // Theme
    currentTheme,
    themes,
    themeLoading,
    loadTenantTheme,
    loadTenantThemes,
    loadActiveTheme,
    updateTenantTheme,
    createTheme,
    updateTheme,
    deleteTheme,
    activateTheme,
    getThemeConfig,

    // Navigation
    navigation,
    navigationLoading,
    loadTenantNavigation,
    updateTenantNavigation,
    getNavigationConfig,

    // User
    user,
    userLoading,
    setUser,

    // Platform
    platformConfig,
    setPlatformConfig,
    initializePlatform,
  };

  return (
    <PlatformContext.Provider value={contextValue}>
      {children}
    </PlatformContext.Provider>
  );
};

export default PlatformContext;
